import { PrevNext } from '@/components/CommonComponents/PrevNext';
import { Stepper } from '@/components/CommonComponents/Stepper';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import BasicDetails from './BasicDetails';
import FinalConfirmation from './FinalConfirmation';
import PasswordSecurity from './PasswordSecurity';
import PermissionAccess from './PermissionAccess';

const roleCategories = ['Admin', 'Manager', 'Editor', 'Viewer'];
const quickTemplates = [
  { title: 'Admin', desc: 'Full management access' },
  { title: 'Manager', desc: 'Department oversight' },
  { title: 'Editor', desc: 'Content management' },
  { title: 'Viewer', desc: 'Read-only access' },
];

// Define the `modules` array with proper types
type Permission = 'view' | 'create' | 'edit' | 'delete';

interface Module {
  name: string;
  description: string;
  permissions: Permission[];
}

const modules: Module[] = [
  {
    name: 'Dashboard',
    description: 'Main dashboard and overview screens',
    permissions: ['view', 'create', 'edit', 'delete'],
  },
  {
    name: 'User Management',
    description: 'Manage system users and their profiles',
    permissions: ['view', 'create', 'edit', 'delete'],
  },
  {
    name: 'Roles & Permissions',
    description: 'Configure roles and permission settings',
    permissions: ['view', 'create', 'edit', 'delete'],
  },
  {
    name: 'Survey Management',
    description: 'Create and manage surveys and questionnaires',
    permissions: ['view', 'create', 'edit', 'delete'],
  },
  {
    name: 'Wallet Management',
    description: 'Handle financial transactions and wallets',
    permissions: ['view', 'create', 'edit', 'delete'],
  },
  {
    name: 'Notifications',
    description: 'Send and manage system notifications',
    permissions: ['view', 'create', 'edit', 'delete'],
  },
  {
    name: 'Reports & Analytics',
    description: 'Generate reports and view analytics',
    permissions: ['view', 'create', 'edit', 'delete'],
  },
  {
    name: 'System Settings',
    description: 'Configure system-wide settings',
    permissions: ['view', 'create', 'edit', 'delete'],
  },
];

const CreateRole: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    roleName: '',
    roleCategory: '',
    roleDescription: '',
    template: '',
  });
  const [step, setStep] = useState(0);
  const [selectedPermissions, setSelectedPermissions] = useState<Record<string, string[]>>({});

  const [accessLevel, setAccessLevel] = useState<'full' | 'restricted' | 'readonly'>('full');
  const [settings, setSettings] = useState({
    mfa: false,
    autoTimeout: false,
    ipRestriction: false,
    auditLogging: false,
    timeRestriction: false,
  });
  const [maxUsers, setMaxUsers] = useState<number | ''>('');
  const [sessionDuration, setSessionDuration] = useState('60');
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTemplateClick = (template: string) => {
    setForm((prev) => ({ ...prev, template }));
  };

  const handlePrev = () => {
    setStep((prevStep) => Math.max(prevStep - 1, 0)); // Prevent going below 0
  };

  const handleNext = () => {
    setStep((prevStep) => Math.min(prevStep + 1, 3)); // Limit to 3 steps
  };

  const handleReset = () => {
    setForm({
      roleName: '',
      roleCategory: '',
      roleDescription: '',
      template: '',
    });
    setSelectedPermissions({});
    setAccessLevel('full');
    setSettings({
      mfa: false,
      autoTimeout: false,
      ipRestriction: false,
      auditLogging: false,
      timeRestriction: false,
    });
    setMaxUsers('');
    setSessionDuration('60');
    setStep(0); // Reset to the first step
  };

  const togglePermission = (module: string, permission: string) => {
    setSelectedPermissions((prev) => {
      const current = prev[module] || [];
      return current.includes(permission)
        ? { ...prev, [module]: current.filter((p) => p !== permission) }
        : { ...prev, [module]: [...current, permission] };
    });
  };

  const toggleSelectAll = (module: string) => {
    setSelectedPermissions((prev) => {
      const all = modules.find((m) => m.name === module)?.permissions || [];
      const current = prev[module] || [];
      return current.length === all.length ? { ...prev, [module]: [] } : { ...prev, [module]: all };
    });
  };

  const handleToggleSetting = (key: keyof typeof settings) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCreate = () => {
    navigate('/dashboard/roles-and-permissions');
  };

  return (
    <div className="p-8 bg-gradient-to-br from-pink-50 to-white min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Create New Role</h1>
          <p className="text-sm text-gray-500">
            Define a new role with specific permissions and access levels
          </p>
        </div>
        <button
          className="text-sm text-pink-700 hover:underline"
          onClick={() => navigate('/dashboard/roles-and-permissions')}
        >
          ⬅ Back to Roles List
        </button>
      </div>

      {/* Step Indicator */}
      <Stepper
        steps={[
          { title: 'Basic Details', subtitle: 'Enter personal information' },
          {
            title: 'Permission & Access',
            subtitle: 'Set user roles and access',
          },
          {
            title: 'Password & Security',
            subtitle: 'Configure login credentials',
          },
          {
            title: 'Final Confirmation',
            subtitle: 'Review and confirm details',
          },
        ]}
        activeStep={step}
      />

      {/* Form Section */}
      <div className="bg-white shadow-md rounded-xl p-6">
        {/* Conditional Rendering of Components */}
        {step === 0 && (
          <BasicDetails
            form={form}
            handleChange={handleChange}
            handleTemplateClick={handleTemplateClick}
          />
        )}
        {step === 1 && (
          <PermissionAccess
            modules={modules}
            selectedPermissions={selectedPermissions}
            togglePermission={togglePermission}
            toggleSelectAll={toggleSelectAll}
          />
        )}
        {step === 2 && (
          <PasswordSecurity
            accessLevel={accessLevel}
            setAccessLevel={setAccessLevel}
            settings={settings}
            handleToggleSetting={handleToggleSetting}
            maxUsers={maxUsers}
            setMaxUsers={setMaxUsers}
            sessionDuration={sessionDuration}
            setSessionDuration={setSessionDuration}
          />
        )}
        {step === 3 && (
          <FinalConfirmation
            form={form}
            selectedPermissions={selectedPermissions}
            accessLevel={accessLevel}
            settings={settings}
            maxUsers={maxUsers}
            sessionDuration={sessionDuration}
            isConfirmed={isConfirmed} // Pass down the confirmation state
            setIsConfirmed={setIsConfirmed} // Provide function to update confirmation state
          />
        )}
      </div>

      {/* Previous, Reset & Next Actions */}
      <PrevNext
        hidePrevious={step === 0}
        prev={handlePrev}
        next={handleNext}
        onReset={handleReset}
        create={step === 3} // Pass `create` as true only on the final step
        createLabel="Create New Admin User →"
        onCreate={handleCreate}
        disableCreate={!isConfirmed} // Disable Create button if not confirmed
      />
    </div>
  );
};

export default CreateRole;
